import React, { useEffect, useState } from "react";
import { apiGet, formatDateTime } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity } from "lucide-react";
import SignalBadge from "@/components/common/SignalBadge";

/**
 * TradingView technical analysis card for a single security.
 * Shows: BUY/SELL/NEUTRAL recommendation, MA + Oscillators sub-recs, RSI gauge,
 * MACD value, Buy/Neutral/Sell counts.
 *
 * All signal badges (overall recommendation + MA + Oscillators) use the
 * shared `SignalBadge` component so colours / pill shape / typography
 * are identical to the AI Signal and Deep AI Signal badges elsewhere
 * in the app. TV labels (NEUTRAL / SELL / STRONG SELL) are folded into
 * the same colour tokens by SignalBadge's MAP — see that component's
 * header for the mapping rationale.
 *
 * RSI gauge is rendered as an SVG arc:
 *  - 0–30: red zone (oversold)
 *  - 30–70: grey zone (neutral)
 *  - 70–100: green zone (overbought)
 */
const RsiGauge = ({ value }) => {
  const v = typeof value === "number" ? Math.max(0, Math.min(100, value)) : null;
  if (v == null) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="rsi-gauge-empty">
        RSI unavailable
      </div>
    );
  }
  // Arc geometry
  const cx = 70, cy = 60, r = 50;
  const angleFor = (val) => Math.PI - (val / 100) * Math.PI; // 0% → π, 100% → 0
  const polar = (val) => {
    const a = angleFor(val);
    return { x: cx + r * Math.cos(a), y: cy - r * Math.sin(a) };
  };
  const arc = (a, b, color) => {
    const p1 = polar(a), p2 = polar(b);
    const large = b - a > 50 ? 1 : 0;
    return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${large} 1 ${p2.x} ${p2.y}`;
  };
  const dot = polar(v);
  const zone =
    v < 30 ? "Oversold" : v > 70 ? "Overbought" : "Neutral";
  const zoneColor =
    v < 30 ? "text-[hsl(var(--destructive))]" :
    v > 70 ? "text-[hsl(var(--success))]" :
    "text-muted-foreground";

  return (
    <div className="flex items-center gap-3" data-testid="rsi-gauge">
      <svg width="140" height="76" viewBox="0 0 140 76">
        {/* Three coloured zones */}
        <path d={arc(0, 30)} fill="none" stroke="hsl(var(--destructive))" strokeWidth="6" strokeOpacity="0.55" />
        <path d={arc(30, 70)} fill="none" stroke="hsl(var(--muted-foreground))" strokeWidth="6" strokeOpacity="0.4" />
        <path d={arc(70, 100)} fill="none" stroke="hsl(var(--success))" strokeWidth="6" strokeOpacity="0.55" />
        {/* Tick labels */}
        <text x="14" y="74" fontSize="9" textAnchor="middle" fill="hsl(var(--muted-foreground))">0</text>
        <text x="70" y="14" fontSize="9" textAnchor="middle" fill="hsl(var(--muted-foreground))">50</text>
        <text x="126" y="74" fontSize="9" textAnchor="middle" fill="hsl(var(--muted-foreground))">100</text>
        {/* Current dot */}
        <circle cx={dot.x} cy={dot.y} r="6" fill="hsl(var(--primary))" stroke="hsl(var(--background))" strokeWidth="2" />
      </svg>
      <div className="space-y-0.5">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">RSI</div>
        <div className="text-xl font-display font-semibold tabular leading-none">{v.toFixed(1)}</div>
        <div className={`text-[11px] ${zoneColor}`}>{zone}</div>
      </div>
    </div>
  );
};

const TechnicalAnalysisCard = ({ cseSymbol }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!cseSymbol) return;
    apiGet(`/technical-signals/${cseSymbol}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoaded(true));
  }, [cseSymbol]);

  if (!loaded) {
    return null;
  }

  if (!data) {
    return (
      <Card className="border bg-card" data-testid="technical-analysis-card-empty">
        <CardHeader className="pb-2">
          <CardTitle className="font-display flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />
            Technical Analysis (TradingView)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            No technical signal yet for {cseSymbol}. The next overnight refresh will populate this.
          </p>
        </CardContent>
      </Card>
    );
  }

  const macdSign = data.macd_value == null ? null : data.macd_value > 0 ? "Positive" : "Negative";

  return (
    <Card className="border bg-card" data-testid="technical-analysis-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="font-display flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />
            Technical Analysis (TradingView)
          </CardTitle>
          <span data-testid="tv-overall-recommendation">
            <SignalBadge signal={data.recommendation} size="md" />
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-md border bg-muted/30 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Moving averages</div>
            <div className="mt-1">
              <SignalBadge signal={data.moving_average_recommendation} size="sm" />
            </div>
          </div>
          <div className="rounded-md border bg-muted/30 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Oscillators</div>
            <div className="mt-1">
              <SignalBadge signal={data.oscillator_recommendation} size="sm" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center pt-1">
          <RsiGauge value={data.rsi} />
          <div className="space-y-1.5">
            <div className="text-xs">
              <span className="text-muted-foreground">MACD: </span>
              <span className={`font-semibold tabular ${macdSign === "Positive" ? "text-[hsl(var(--success))]" : macdSign === "Negative" ? "text-[hsl(var(--destructive))]" : ""}`}>
                {macdSign || "—"}
              </span>
              {data.macd_value != null && (
                <span className="text-muted-foreground"> ({data.macd_value.toFixed(3)})</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">
                Buy: {data.buy ?? 0}
              </Badge>
              <Badge variant="outline" className="bg-muted text-muted-foreground">
                Neutral: {data.neutral ?? 0}
              </Badge>
              <Badge variant="outline" className="bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30">
                Sell: {data.sell ?? 0}
              </Badge>
            </div>
          </div>
        </div>

        {data.low_volume_warning && (
          <div
            className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-[11px] text-[hsl(var(--warning))]"
            data-testid="tv-low-volume-warning"
          >
            <span aria-hidden>⚠</span>
            <span>
              Low volume — average daily volume {data.avg_volume_30d ? Math.round(data.avg_volume_30d).toLocaleString() : "—"} over
              the last 30 days. Technical signal may be unreliable.
            </span>
          </div>
        )}

        <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t">
          <span>
            Updated: {data.updated_at ? formatDateTime(data.updated_at) : "—"}
          </span>
          <span>
            Source:{" "}
            <a
              href={`https://www.tradingview.com/symbols/CSELK-${cseSymbol}/technicals/`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              TradingView
            </a>
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default TechnicalAnalysisCard;
